import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  Trophy, 
  PlusCircle, 
  ArrowLeft, 
  ChevronRight,
  X,
  ChevronDown,
  Calendar,
  Coins,
  Wallet
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import StrokePlayEventDetail from "../components/StrokePlayEventDetail";
import { CourseLookupModal } from "../components/score-entry";
import EntryFeeSelector from "../components/EntryFeeSelector";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Preset game names for quick selection
const GAME_NAME_OPTIONS = [
  "Weekend Round",
  "Medal Play",
  "Club Championship", 
  "Stroke Battle",
  "The Classic",
  "Low Gross Challenge"
];

// Payout structure options
const PAYOUT_OPTIONS = [
  { value: "none", label: "No Payouts", description: "Just for fun" },
  { value: "winner", label: "Winner Takes All", description: "First place wins pot" },
  { value: "split", label: "Split (60/30/10)", description: "Top 3 places" }
];

// Create Stroke Play Game Modal - Similar to Skins Flow
const CreateStrokePlayModal = ({ onClose, onCreated }) => {
  const [step, setStep] = useState('details'); // 'details', 'course'
  const [name, setName] = useState('');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [holes, setHoles] = useState(18);
  const [startingNine, setStartingNine] = useState('front'); // 'front' or 'back'
  const [entryFee, setEntryFee] = useState(0);
  const [entryFeeType, setEntryFeeType] = useState('coins');
  const [payoutStructure, setPayoutStructure] = useState('none');
  const [useHandicaps, setUseHandicaps] = useState(false);
  const [courseData, setCourseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // After course is selected, create game immediately
  const handleCourseSelect = async (course) => {
    setCourseData(course);
    
    if (!name.trim()) {
      toast.error("Please select a game name");
      setStep('details');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/stroke-play/events`, {
        name: name.trim(),
        date,
        course_name: course?.name || null,
        course_id: course?.course_id || null,
        hole_pars: course?.hole_pars || [],
        hole_handicaps: course?.hole_handicaps || [],
        holes,
        starting_nine: holes === 9 ? startingNine : 'front',
        entry_fee: entryFee,
        entry_fee_type: entryFee > 0 ? entryFeeType : null,
        payout_structure: payoutStructure,
        use_handicaps: useHandicaps
      });
      toast.success("Game created! Now add players.");
      
      // Dispatch event to refresh Golf Coins balance in header (coins deducted on create)
      if (entryFee > 0 && entryFeeType === 'coins') {
        window.dispatchEvent(new Event('golfCoinsUpdated'));
      }
      
      onCreated(response.data.event_id, true);
    } catch (error) {
      toast.error("Failed to create stroke play game");
      setStep('details');
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Basic Details
  if (step === 'details') {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-start justify-center z-50 p-4 pt-20 overflow-y-auto">
        <Card className="w-full max-w-md animate-fade-in">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Trophy className="w-5 h-5 text-blue-600" />
                New Stroke Play Game
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
                <X className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Date - Compact at top */}
            <div className="flex items-center justify-between bg-stone-50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-stone-600">
                <Calendar className="w-4 h-4" />
                <span className="text-sm font-medium">Date</span>
              </div>
              <input 
                type="date"
                value={date} 
                onChange={(e) => setDate(e.target.value)}
                className="text-sm font-medium text-stone-900 bg-transparent border-none focus:outline-none cursor-pointer"
                data-testid="strokeplay-date-input"
              />
            </div>
            
            {/* Game Name Dropdown */}
            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block">Game Name</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="w-full p-3 border border-stone-300 rounded-lg bg-white flex items-center justify-between hover:border-blue-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  data-testid="strokeplay-name-dropdown"
                >
                  <span className={name ? 'text-stone-900 font-medium' : 'text-stone-400'}>
                    {name || 'Select game name...'}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-stone-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg py-1 max-h-60 overflow-auto">
                    {GAME_NAME_OPTIONS.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => { setName(option); setShowDropdown(false); }}
                        className={`w-full px-4 py-2.5 text-left hover:bg-blue-50 transition-colors ${name === option ? 'bg-blue-100 text-blue-700 font-medium' : 'text-stone-700'}`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Holes Selection */}
            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block">Holes</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setHoles(9)}
                  className={`p-3 rounded-lg border-2 font-medium transition-all ${
                    holes === 9
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-stone-200 hover:border-stone-300 text-stone-600'
                  }`}
                  data-testid="holes-9"
                >
                  9 Holes
                </button>
                <button
                  type="button"
                  onClick={() => setHoles(18)}
                  className={`p-3 rounded-lg border-2 font-medium transition-all ${
                    holes === 18
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-stone-200 hover:border-stone-300 text-stone-600'
                  }`}
                  data-testid="holes-18"
                >
                  18 Holes
                </button>
              </div>
            </div>
            
            {/* Starting Nine - Only show for 9 hole games */}
            {holes === 9 && (
              <div>
                <label className="text-sm font-medium text-stone-700 mb-2 block">Starting Nine</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setStartingNine('front')}
                    className={`p-3 rounded-lg border-2 font-medium transition-all ${
                      startingNine === 'front'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-stone-200 hover:border-stone-300 text-stone-600'
                    }`}
                    data-testid="starting-nine-front"
                  >
                    Front 9 (1-9)
                  </button>
                  <button
                    type="button"
                    onClick={() => setStartingNine('back')}
                    className={`p-3 rounded-lg border-2 font-medium transition-all ${
                      startingNine === 'back'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-stone-200 hover:border-stone-300 text-stone-600'
                    }`}
                    data-testid="starting-nine-back"
                  >
                    Back 9 (10-18)
                  </button>
                </div>
              </div>
            )}
            
            {/* Handicap Toggle */}
            <div className="flex items-center justify-between bg-stone-50 rounded-lg p-3">
              <div>
                <p className="text-sm font-medium text-stone-700">Use Handicaps</p>
                <p className="text-xs text-stone-500">Calculate net scores for fair competition</p>
              </div>
              <button
                type="button"
                onClick={() => setUseHandicaps(!useHandicaps)}
                className={`w-12 h-6 rounded-full transition-colors relative ${useHandicaps ? 'bg-green-500' : 'bg-stone-300'}`}
                data-testid="use-handicaps-toggle"
              >
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${useHandicaps ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
            
            {/* Entry Fee - Golf Coins or Wallet */}
            <div className="border-t pt-4">
              <label className="text-sm font-medium text-stone-700 mb-3 block flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-500" />
                Entry Fee (Optional)
              </label>
              <EntryFeeSelector
                entryFee={entryFee}
                entryFeeType={entryFeeType}
                onChangeValue={setEntryFee}
                onChangeType={setEntryFeeType}
                playerCount={4}
                showTotalPot={true}
                compact={true}
              />
            </div>
            
            {/* Payout Structure - Only show if entry fee > 0 */}
            {entryFee > 0 && (
              <div>
                <label className="text-sm font-medium text-stone-700 mb-2 block">Payout Structure</label>
                <div className="space-y-2">
                  {PAYOUT_OPTIONS.filter(p => p.value !== 'none').map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setPayoutStructure(option.value)}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                        payoutStructure === option.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-stone-200 hover:border-stone-300'
                      }`}
                      data-testid={`payout-${option.value}`}
                    >
                      <p className={`font-medium ${payoutStructure === option.value ? 'text-blue-700' : 'text-stone-700'}`}>
                        {option.label}
                      </p>
                      <p className="text-xs text-stone-500">{option.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
          <div className="px-6 pb-6">
            <Button 
              onClick={() => setStep('course')} 
              disabled={!name || loading} 
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-base font-medium"
              data-testid="create-game-next-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                'Select Course & Create Game'
              )}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Step 2: Course Selection
  if (step === 'course') {
    return (
      <CourseLookupModal
        isOpen={true}
        onClose={() => setStep('details')}
        onCourseSelect={handleCourseSelect}
        title="Find Course for Stroke Play"
      />
    );
  }

  return null;
};

const StrokePlayPage = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const selectedEvent = searchParams.get('event');
  const setSelectedEvent = eventId => setSearchParams(eventId ? { event: eventId } : {});
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/stroke-play/events`);
      setEvents(response.data.events || []);
    } catch (err) {
      console.error('Failed to fetch stroke play events:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedEvent) fetchEvents();
  }, [selectedEvent]);

  if (selectedEvent) {
    return <StrokePlayEventDetail key={selectedEvent} eventId={selectedEvent} onBack={() => setSelectedEvent(null)} user={user} />;
  }

  return (
    <div className="ps-theme-page max-w-4xl mx-auto animate-fade-in pt-2" data-testid="stroke-play-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/tournaments')} className="p-2">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-stone-900 flex items-center gap-2">
            <Trophy className="w-6 h-6 text-blue-600" />
            Stroke Play
          </h1>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
          <PlusCircle className="w-4 h-4 mr-2" />
          Create Game
        </Button>
      </div>

      {/* Content */}
      {loading ? (
        <Card className="p-8">
          <div className="flex items-center justify-center">
            <div className="spinner w-8 h-8 border-blue-600 border-t-transparent" />
          </div>
        </Card>
      ) : events.length === 0 ? (
        <Card className="p-8 text-center">
          <Trophy className="w-16 h-16 mx-auto mb-4 text-stone-300" />
          <h2 className="text-xl font-bold text-stone-900 mb-2">No Stroke Play Games Yet</h2>
          <p className="text-stone-500 mb-6 max-w-md mx-auto">
            Create your first Stroke Play game - compete for the lowest total score over 9 or 18 holes. Track gross and net scores with optional handicaps.
          </p>
          <Button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
            <PlusCircle className="w-4 h-4 mr-2" />
            Create Your First Stroke Play Game
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {events.map((event) => (
            <Card 
              key={event.event_id} 
              className="cursor-pointer hover:border-blue-300 hover:shadow-md transition-all"
              onClick={() => setSelectedEvent(event.event_id)}
              data-testid={`event-card-${event.event_id}`}
              role="button" tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedEvent(event.event_id); } }}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      event.status === 'active' ? 'bg-blue-100' : 
                      event.status === 'completed' ? 'bg-green-100' : 'bg-stone-100'
                    }`}>
                      <Trophy className={`w-6 h-6 ${
                        event.status === 'active' ? 'text-blue-600' : 
                        event.status === 'completed' ? 'text-green-600' : 'text-stone-500'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-stone-900">{event.name}</h3>
                      <p className="text-sm text-stone-500">
                        {event.player_count} players • {event.holes} holes • {event.date}
                      </p>
                      {event.use_handicaps && (
                        <span className="text-xs text-blue-600 font-medium">Net scoring enabled</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {event.entry_fee > 0 && (
                      <span className="text-xs font-medium text-green-600">${event.entry_fee}</span>
                    )}
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      event.status === 'active' ? 'bg-blue-100 text-blue-700' :
                      event.status === 'completed' ? 'bg-green-100 text-green-700' :
                      'bg-stone-100 text-stone-600'
                    }`}>
                      {event.status === 'active' ? 'In Progress' : event.status === 'completed' ? 'Completed' : 'Setup'}
                    </span>
                    <ChevronRight className="w-5 h-5 text-stone-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateStrokePlayModal 
          onClose={() => setShowCreateModal(false)} 
          onCreated={(eventId, openAddPlayers) => {
            setShowCreateModal(false);
            if (openAddPlayers) {
              navigate(`/tournaments/stroke-play?event=${eventId}&addPlayers=true`);
            } else setSelectedEvent(eventId);
            fetchEvents();
          }}
        />
      )}
    </div>
  );
};

export default StrokePlayPage;
